import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { useTemplates } from '../hooks/useTemplates';
import { Button } from '../components/ui/button';
import { ArrowLeft, Box, Loader2, Package } from 'lucide-react';

const TemplatesPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { templates, loading } = useTemplates();

  return (
    <div className="min-h-screen bg-white" data-testid="templates-page">
      <header className="border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="text-gray-600 hover:text-gray-900"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('common.back')}
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('templates.title')}</h1>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-24 border-2 border-dashed border-gray-200 rounded-lg">
            <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Package className="w-7 h-7 text-gray-400" strokeWidth={1.5} />
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">{t('templates.empty')}</h3>
            <p className="text-sm text-gray-500">{t('templates.empty_desc')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {templates.map((template) => (
              <div
                key={template.id}
                className="bg-white border border-gray-200 rounded-lg p-5 hover:border-blue-500 hover:shadow-md transition-all cursor-pointer group"
                onClick={() => navigate(`/new-project?template=${template.id}`)}
                data-testid={`template-card-${template.id}`}
              >
                <div className="aspect-square bg-gray-50 rounded-lg mb-4 flex items-center justify-center">
                  {template.texture ? (
                    <img
                      src={template.texture}
                      alt={template.name}
                      className="w-full h-full object-contain p-4"
                      style={{ imageRendering: 'pixelated' }}
                    />
                  ) : (
                    <Box className="w-12 h-12 text-gray-300" strokeWidth={1.5} />
                  )}
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1 truncate">
                  {template.name}
                </h3>
                <p className="text-xs text-gray-500 truncate mb-2">{template.identifier}</p>
                <span className="inline-block px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                  {template.category}
                </span>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default TemplatesPage;
